import type { LitContracts } from '@lit-protocol/contracts-sdk'
import type {
  AuthSig,
  SessionKeySignedMessage,
  SessionSigsMap
} from '@lit-protocol/types'

/**
 *
 * Extracts the first SessionSig from the SessionSigsMap since we only pass a single SessionSig to the AWS endpoint
 *
 * @param pkpSessionSigs - The PKP sessionSigs (map) used to associate the PKP with the generated private key
 *
 * @returns { AuthSig } - The first SessionSig from the map
 */
export function getFirstSessionSig(pkpSessionSigs: SessionSigsMap): AuthSig {
  const sessionSigsEntries = Object.entries(pkpSessionSigs)

  if (sessionSigsEntries.length === 0) {
    throw new Error(
      `Invalid pkpSessionSigs, length zero: ${JSON.stringify(pkpSessionSigs)}`,
    )
  }

  const firstEntry = sessionSigsEntries[0]
  if (!firstEntry) {
    throw new Error(
      `Invalid pkpSessionSigs, first entry is undefined: ${JSON.stringify(pkpSessionSigs)}`,
    )
  }

  const [, sessionSig] = firstEntry

  return sessionSig
}

/**
 * Extracts the wallet address from an individual SessionSig
 *
 * @param pkpSessionSig - The first PKP sessionSig from the function getFirstSessionSig
 *
 * @returns { string } - The wallet address that signed the capabilites AuthSig (BLS)
 */
export function getPkpAddressFromSessionSig(pkpSessionSig: AuthSig): string {
  const sessionSignedMessage: SessionKeySignedMessage = JSON.parse(
    pkpSessionSig.signedMessage,
  )

  const capabilities = sessionSignedMessage.capabilities

  if (!capabilities || capabilities.length === 0) {
    throw new Error(
      `Capabilities in the session's signedMessage is empty, but required.`,
    )
  }

  const delegationAuthSig = capabilities.find(({ algo }) => algo === 'LIT_BLS')

  if (!delegationAuthSig) {
    throw new Error(
      'SessionSig is not from a PKP; no LIT_BLS capabilities found',
    )
  }

  const pkpAddress = delegationAuthSig.address

  return pkpAddress
}

export function getPkpAddressFromSessionSigs(sessionSigs: AuthSig[]): string {
  if (sessionSigs.length === 0) {
    throw new Error('Cannot get PKP address from empty sessionSigs')
  }
  return getPkpAddressFromSessionSig(sessionSigs[0]!)
}


// The maximum number of PKPs to iterate through to get the token ID
const MAX_GET_PKP_TOKEN_ID_ATTEMPTS = 10

/**
 * Gets the token ID of the PKP from the wallet address
 *
 * This function iterates through all the PKPs owned by the wallet address
 * and returns the token ID of the PKP that has the same public key as the wallet address.
 * If no PKP is found, it throws an error. We limit the number of attempts to prevent large loops.
 *
 * @param litContracts - The LitContracts instance
 * @param pkpAddress - The wallet address of the PKP
 * @returns The token ID of the PKP
 */
export async function getPKPTokenIdFromPKPAddress(
  litContracts: LitContracts,
  pkpAddress: string,
) {
  const ownedPKPs = await litContracts.pkpNftContract.read.balanceOf(pkpAddress)
  if (ownedPKPs.gt(MAX_GET_PKP_TOKEN_ID_ATTEMPTS)) {
    console.warn(
      `More than ${MAX_GET_PKP_TOKEN_ID_ATTEMPTS} (${ownedPKPs.toString()}) PKPs owned by ${pkpAddress}, limiting attempts to ${MAX_GET_PKP_TOKEN_ID_ATTEMPTS}`,
    )
  }

  for (
    let i = 0;
    i < Math.min(ownedPKPs.toNumber(), MAX_GET_PKP_TOKEN_ID_ATTEMPTS);
    i++
  ) {
    const pkpTokenId =
      await litContracts.pkpNftContract.read.tokenOfOwnerByIndex(pkpAddress, i)
    const pkpEthAddress =
      await litContracts.pkpNftContract.read.getEthAddress(pkpTokenId)
    if (pkpEthAddress === pkpAddress) {
      return pkpTokenId.toString()
    }
  }

  // If no PKP is found, we try to find the PKP that is owned by the backend wallet address
  const backendWalletAddress = await litContracts.signer.getAddress()
  const backendOwnedPKPs =
    await litContracts.pkpNftContract.read.balanceOf(backendWalletAddress)
  for (
    let i = 0;
    i < Math.min(backendOwnedPKPs.toNumber(), MAX_GET_PKP_TOKEN_ID_ATTEMPTS);
    i++
  ) {
    const pkpTokenId =
      await litContracts.pkpNftContract.read.tokenOfOwnerByIndex(
        backendWalletAddress,
        i,
      )

    const pkpEthAddress =
      await litContracts.pkpNftContract.read.getEthAddress(pkpTokenId)
    if (pkpEthAddress === pkpAddress) {
      return pkpTokenId.toString()
    }
  }

  throw new Error('PKP not found')
}
